import { BoardID, createBoardID } from 'BoardID';
import {
  changePatternSize,
  makeMaterialPatternFragment,
  MaterialPatternString,
  parseMaterialPatternString,
  parseSizeOnlyPatternString,
  scalePatternString,
  toPatternString,
} from 'Pattern';
import {
  BoardDetails,
  BoardType,
  Dimensions2D,
  Direction,
  Material,
  QuarterSquareParams,
  Renderable,
  Settings,
  TwoArray,
  Units,
} from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import Strips from 'boardClasses/strips/Strips';
import {
  fillRenderablesAt,
  maxBy,
  randomInt,
  sample,
  scaleNumber,
  sortBy,
  sumBy,
  without,
} from 'utils/utils';

export default class QuarterSquare extends BoardBase<BoardType.QuarterSquare> {
  constructor(
    id: BoardID,
    name: string,
    params: QuarterSquareParams,
    units?: Units
  ) {
    super(BoardType.QuarterSquare, id, name, params, units);
  }

  static createRandom(
    materials: TwoArray<Material>,
    size: Dimensions2D
  ): QuarterSquareParams {
    const wood1 = sample(materials);
    const wood2 = sample(without(materials, (material) => material !== wood1));

    const centerBlockSize = randomInt(1, 8) / 4;
    const restOfPatternLength = (size.width - centerBlockSize) / 2;

    let pattern = `${centerBlockSize}` as MaterialPatternString;

    let sizeAcc = 0;

    while (sizeAcc < restOfPatternLength) {
      const randomSize = randomInt(1, 8) / 4;
      const size =
        randomSize + sizeAcc <= restOfPatternLength
          ? randomSize
          : restOfPatternLength - sizeAcc;

      pattern = `${size} ${pattern}` as MaterialPatternString;
      sizeAcc += size;
    }

    return {
      wood1,
      wood2,
      pattern,
      thickness: 1,
    };
  }

  getDimensions(): Dimensions2D {
    const pattern = parseMaterialPatternString(this.params.pattern);
    const lastPatternFragment = pattern[pattern.length - 1];
    const lastPatternFragmentSize =
      lastPatternFragment == null ? 0 : lastPatternFragment.size;
    const size =
      2 * sumBy(pattern.slice(0, -1), ({ size }) => size) +
      lastPatternFragmentSize;

    return { width: size, height: size };
  }

  getRenderables(): Array<Renderable<BoardType.QuarterSquare>> {
    const { wood1, wood2 } = this.params;
    const pattern = parseSizeOnlyPatternString(this.params.pattern).map(
      ({ size }, index) =>
        makeMaterialPatternFragment(size, index % 2 ? wood2 : wood1)
    );
    const combinedPattern = [...pattern, ...pattern.slice(0, -1).reverse()];
    const alternateCombinedPattern = combinedPattern.map(({ size }, index) =>
      makeMaterialPatternFragment(size, index % 2 ? wood1 : wood2)
    );

    const renderables: Array<Renderable<BoardType.QuarterSquare>> = [];

    let topOffset = 0;

    for (let rowIndex = 0; rowIndex < combinedPattern.length; ++rowIndex) {
      const adjustedCombinedPattern =
        rowIndex % 2 ? alternateCombinedPattern : combinedPattern;
      const { size } = adjustedCombinedPattern[rowIndex];

      fillRenderablesAt(
        renderables,
        adjustedCombinedPattern,
        topOffset,
        0,
        size,
        Direction.H,
        ({ fragmentIndex, renderableIndex }) => ({
          type: BoardType.QuarterSquare,
          rowIndex:
            rowIndex >= pattern.length
              ? adjustedCombinedPattern.length - rowIndex - 1
              : rowIndex,
          colIndex:
            fragmentIndex >= pattern.length
              ? adjustedCombinedPattern.length - fragmentIndex - 1
              : fragmentIndex,
          renderableIndex,
        })
      );

      topOffset += adjustedCombinedPattern[rowIndex].size;
    }

    return renderables;
  }

  getIsRenderableHighlighted(
    renderable: Renderable<BoardType.QuarterSquare>,
    selection: Renderable<BoardType.QuarterSquare>
  ): boolean {
    return false;
  }

  getBoardDetails(settings: Settings): BoardDetails {
    const { thickness, wood1, wood2 } = this.params;
    const pattern = parseMaterialPatternString(this.params.pattern).map(
      (fragment, index) => ({
        size: fragment.size,
        key: index % 2 ? wood2 : wood1,
        flip: false,
      })
    );
    const maxRowSize = maxBy(pattern, ({ size }) => size) || 0;
    const rowFragments = sortBy([...pattern, ...pattern.slice(0, -1)], ['size'])
      .reverse()
      .slice(0, -1);

    let numRowsNeeded = 1;

    while (rowFragments.length > 0) {
      const lastFragment = rowFragments[rowFragments.length - 1];
      if (
        lastFragment &&
        lastFragment !== rowFragments[0] &&
        maxRowSize - rowFragments[0].size - lastFragment.size >= 0
      ) {
        rowFragments.pop();
      }

      rowFragments.shift();
      numRowsNeeded++;
    }

    return new Strips(
      createBoardID(),
      '',
      {
        thickness: maxRowSize,
        rowHeight: thickness,
        numRows: numRowsNeeded,
        pattern: toPatternString([...pattern, ...pattern.slice(0, -1)]),
        flip: false,
      },
      Units.IN
    ).getBoardDetails(settings);
  }

  handleSelectedSizeChange(
    { metadata }: Renderable<BoardType.QuarterSquare>,
    size: number
  ) {
    const { colIndex } = metadata;

    return this.updateParams({
      pattern: changePatternSize(this.params.pattern, colIndex, size),
    });
  }

  handleSelectedMaterialChange(
    { metadata }: Renderable<BoardType.QuarterSquare>,
    material: Material
  ) {
    const { rowIndex, colIndex } = metadata;
    const woodProp = rowIndex % 2 === colIndex % 2 ? 'wood1' : 'wood2';

    return this.updateParams({
      [woodProp]: material,
    });
  }

  previewSelectionToParamsPanelSelection() {
    return null;
  }

  paramsPanelSelectionToPreviewSelection() {
    return null;
  }

  scaleParams(
    params: QuarterSquareParams,
    scaleFactor: number
  ): QuarterSquareParams {
    return {
      ...params,
      thickness: scaleNumber(params.thickness, scaleFactor),
      pattern: scalePatternString(params.pattern, scaleFactor),
    };
  }
}
